import {
  ArrowRight,
  CalendarCheck,
  Check,
  CheckCircle2,
  Clock3,
  Dumbbell,
  HandHeart,
  HeartHandshake,
  Home,
  Languages,
  MapPin,
  MessageSquareText,
  MoveUpRight,
  Quote,
  ShieldCheck,
  Sparkles,
  UserRoundCheck,
} from "lucide-react";
import { businessConfig } from "../content/siteContent";
import { useLanguage } from "./LanguageProvider";
import { BookingLink, IconText, ResponsiveImage, SectionHeading, SmsLink } from "./ui";

export function Hero() {
  const { copy } = useLanguage();
  return (
    <section className="hero" id="top">
      <div className="container hero-grid">
        <div className="hero-copy">
          <p className="eyebrow">{copy.hero.eyebrow}</p>
          <h1>{copy.hero.headline.split("\n").map((line) => <span key={line}>{line}</span>)}</h1>
          <p className="hero-lede">{copy.hero.copy}</p>
          <div className="button-row">
            <BookingLink event="hero_booking_click">{copy.common.book}</BookingLink>
            <SmsLink>{copy.common.text}</SmsLink>
          </div>
          <p className="hero-trust"><ShieldCheck size={17} aria-hidden="true" />{copy.hero.trust}</p>
        </div>
        <div className="hero-media">
          <ResponsiveImage src={businessConfig.images.hero} alt={copy.hero.imageAlt} eager />
          <div className="hero-note">
            <span><Home size={18} aria-hidden="true" /></span>
            <p><strong>{copy.benefits.items[0][0]}</strong>{copy.benefits.items[0][1]}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Benefits() {
  const { copy } = useLanguage();
  const icons = [Home, UserRoundCheck, HandHeart, Languages];
  return (
    <section className="benefits" aria-labelledby="benefits-title">
      <div className="container">
        <p id="benefits-title" className="sr-only">{copy.benefits.label}</p>
        <div className="benefit-list">
          {copy.benefits.items.map(([title, text], index) =>
            <IconText key={title} icon={icons[index]} title={title} text={text} />
          )}
        </div>
      </div>
    </section>
  );
}

export function ProblemStatement() {
  const { copy } = useLanguage();
  return (
    <section className="section problem">
      <div className="container split-layout">
        <div className="problem-media">
          <ResponsiveImage src={businessConfig.images.movement} alt={copy.problem.imageAlt} />
          <span className="image-caption"><MoveUpRight size={18} aria-hidden="true" />{copy.problem.eyebrow}</span>
        </div>
        <div className="split-copy">
          <SectionHeading eyebrow={copy.problem.eyebrow} title={copy.problem.title} copy={copy.problem.copy} />
          <div className="accent-rule" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}

export function Conditions() {
  const { copy } = useLanguage();
  return (
    <section className="section conditions" id="conditions">
      <div className="container">
        <SectionHeading eyebrow={copy.conditions.eyebrow} title={copy.conditions.title} copy={copy.conditions.copy} />
        <ul className="condition-grid">
          {copy.conditions.items.map((item, index) => (
            <li key={item}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <p>{item}</p>
              <Check size={18} aria-hidden="true" />
            </li>
          ))}
        </ul>
        <p className="small-note">{copy.conditions.disclaimer}</p>
      </div>
    </section>
  );
}

export function ServiceDetails() {
  const { copy } = useLanguage();
  return (
    <section className="section service-wrap" id="services">
      <div className="container service-panel">
        <div className="service-main">
          <p className="eyebrow light">{copy.service.eyebrow}</p>
          <h2>{copy.service.title}<span>{copy.service.chineseTitle}</span></h2>
          <p>{copy.service.copy}</p>
          <BookingLink event="service_booking_click">{copy.service.book}</BookingLink>
        </div>
        <div className="service-meta">
          <p className="price">{copy.service.priceLabel}</p>
          <div><span><Clock3 size={18} aria-hidden="true" />{copy.service.duration}</span><span><Home size={18} aria-hidden="true" />{copy.service.location}</span></div>
        </div>
        <div className="appointment-note">
          <span className="note-icon"><MessageSquareText size={22} aria-hidden="true" /></span>
          <div><h3>{copy.service.noteLabel}</h3><p>{copy.service.note}</p></div>
        </div>
      </div>
    </section>
  );
}

export function NewClientOffer() {
  const { copy } = useLanguage();
  return (
    <section className="section offer-section">
      <div className="container offer">
        <div className="offer-mark" aria-hidden="true"><Sparkles /></div>
        <div>
          <p className="eyebrow">{copy.offer.eyebrow}</p>
          <h2>{copy.offer.title}</h2>
          <p>{copy.offer.copy}</p>
          <span className="fine-print">{copy.offer.finePrint}</span>
        </div>
        <BookingLink event="new_client_booking_click">{copy.offer.book}</BookingLink>
      </div>
    </section>
  );
}

export function HowItWorks() {
  const { copy } = useLanguage();
  const icons = [CalendarCheck, MessageSquareText, CheckCircle2, Home];
  return (
    <section className="section process">
      <div className="container">
        <SectionHeading eyebrow={copy.process.eyebrow} title={copy.process.title} />
        <ol className="process-list">
          {copy.process.steps.map(([title, text], index) => {
            const Icon = icons[index];
            return (
              <li key={title}>
                <div className="step-icon"><Icon aria-hidden="true" /></div>
                <span className="step-number">{String(index + 1).padStart(2, "0")}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

export function AboutApril() {
  const { copy } = useLanguage();
  return (
    <section className="section about" id="about">
      <div className="container split-layout about-grid">
        <div className="about-portrait">
          {businessConfig.april.photo
            ? <ResponsiveImage src={businessConfig.april.photo} alt={copy.about.imageAlt} />
            : <div className="photo-placeholder" role="img" aria-label={copy.about.imageAlt}>
                <span>A</span><p>{copy.about.placeholder}</p>
              </div>}
          <div className="honesty-note"><HeartHandshake aria-hidden="true" /><span>{copy.about.title}</span></div>
        </div>
        <div className="split-copy">
          <SectionHeading eyebrow={copy.about.eyebrow} title={copy.about.title} />
          {copy.about.copy.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          {businessConfig.april.credentials.length > 0
            ? <ul className="credentials">{businessConfig.april.credentials.map((item) => <li key={item}>{item}</li>)}</ul>
            : <p className="credential-placeholder">{copy.about.credentialsLabel}</p>}
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  const { copy } = useLanguage();
  const [featured, ...supporting] = copy.testimonials.items;
  return (
    <section className="section testimonials" id="reviews">
      <div className="container">
        <SectionHeading eyebrow={copy.testimonials.eyebrow} title={copy.testimonials.title} />
        <div className="testimonial-layout">
          <figure className="featured-quote">
            <Quote size={34} aria-hidden="true" />
            <blockquote>“{featured.quote}”</blockquote>
            <figcaption><strong>{featured.name}</strong><span>{featured.location}</span></figcaption>
          </figure>
          <div className="supporting-quotes">
            {supporting.map((item) => (
              <figure key={item.name}>
                <blockquote>“{item.quote}”</blockquote>
                <figcaption><strong>{item.name}</strong><span>{item.location}</span></figcaption>
              </figure>
            ))}
          </div>
        </div>
        <p className="small-note">{copy.testimonials.disclaimer}</p>
      </div>
    </section>
  );
}

export function TreatmentExpectations() {
  const { copy } = useLanguage();
  return (
    <section className="section expectations">
      <div className="container expectation-inner">
        <div className="expectation-icon"><Dumbbell aria-hidden="true" /></div>
        <SectionHeading eyebrow={copy.expectations.eyebrow} title={copy.expectations.title} copy={copy.expectations.copy} />
      </div>
    </section>
  );
}

export function ServiceArea() {
  const { copy } = useLanguage();
  return (
    <section className="section area">
      <div className="container area-grid">
        <div>
          <SectionHeading eyebrow={copy.area.eyebrow} title={copy.area.title} copy={copy.area.copy} />
          <SmsLink>{copy.common.text}</SmsLink>
        </div>
        <div className="area-visual" aria-hidden="true">
          <div className="map-ring ring-one" />
          <div className="map-ring ring-two" />
          <MapPin size={42} strokeWidth={1.6} />
          <span>Orange County</span>
        </div>
        <p className="area-note">{copy.area.testimonialNote}</p>
      </div>
    </section>
  );
}

export function FinalCTA() {
  const { copy } = useLanguage();
  return (
    <section className="section final-cta">
      <div className="container final-cta-inner">
        <p className="eyebrow light">{copy.finalCta.eyebrow}</p>
        <h2>{copy.finalCta.title}</h2>
        <p>{copy.finalCta.copy}</p>
        <div className="button-row">
          <BookingLink event="final_booking_click">{copy.common.book}</BookingLink>
          <SmsLink>{copy.common.text}</SmsLink>
        </div>
        <ArrowRight className="cta-arrow" aria-hidden="true" />
      </div>
    </section>
  );
}
